import WaveformCanvas from 'components/WaveformPlayer/WaveformCanvas'
import { useEffect, useRef, useState } from 'react'
import { ReactComponent as Play } from 'assets/icons/play.svg'
import { ReactComponent as Pause } from 'assets/icons/pause.svg'
import Duration from 'components/WaveformPlayer/Duration'
import Volume from 'components/WaveformPlayer/Volume'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import {
  CherryBlossomParticleSystem,
  createParticleSystem,
  RainParticleSystem,
} from './ParticleSystems'
import clsx from 'clsx'
import {
  fallSystem,
  fireCenteredSystem,
  fireSystem,
  firewaveSystem,
  sandSystem,
} from '../../views/TestView/ParticleCanvas'

/**
 * Cache buffer
 * fix loops current time
 * How to handle uploaded but unprocesses files on page refresh
 * cancel song load if you leave track after hitting play
 */

const defaultVolume = window.localStorage.getItem('volume') || 0.75
const WaveformPlayer = ({ track, waveformData, particleSystemData }) => {
  const playerRef = useRef({
    ellapsedTime: 0,
    startTime: 0,
    pausedTime: 0,
    state: 'paused',
    trackId: track.id,
    songLookaheadTrigger: true,
    getCurrentTime: getCurrentTime,
  })
  const player = playerRef.current

  const foregroundParticleCanvasRef = useRef(null)
  const backgroundParticleCanvasRef = useRef(null)
  const soundContainer = useRef(null)

  const [currentPosition, setCurrentPosition] = useState(1)
  const [loading, setLoading] = useState(false)
  const [mousePosition, setMousePosition] = useState(0)
  const [volume, setVolume] = useState(defaultVolume)

  const duration = track?.duration / 1000
  playerRef.current.duration = duration
  playerRef.current.getDuration = () => duration
  const playbackEnabled = track.fileConversionResult === 'success'
  const fgColor = track?.data?.theme?.highlightColor
  const { loopFrom, loopTo, count, loopPointType } = track?.data?.loop || {}

  useEffect(() => {
    const interval = setInterval(() => {
      const newPosition = player.audioContext ? getCurrentTime() / duration : 1
      if (newPosition !== currentPosition) {
        setCurrentPosition(newPosition)
      }
    }, 27)
    return () => clearInterval(interval)
  }, [currentPosition, duration])

  useEffect(() => {
    const interval = setInterval(() => {
      if (player.source) {
        const currentTime = getCurrentTime()
        const { loop, loopStart, loopEnd } = player.source
        if (
          loop &&
          currentTime + 0.1 > (loopEnd || player.source.buffer.duration)
        ) {
          player.ellapsedTime = loopStart
          player.startTime = player.audioContext.currentTime
        }
      }
    }, 27)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (soundContainer.current) {
      soundContainer.current.volume = volume
    }
  }, [soundContainer.current])

  useEffect(() => {
    return () => {
      if (player.audioContext) {
        player.audioContext.suspend()
      }
    }
  }, [player])

  useEffect(() => {
    if (player?.source) {
      const loop = track?.data?.loop || {}
      setupLoop(loop)
    }
  }, [player, loopFrom, loopTo, count, loopPointType])

  useEffect(() => {
    return () => {
      player.unmounted = true
      if (playerRef.current.audioContext) {
        playerRef.current.audioContext.suspend()
      }
      if (player.request) {
        player.request.abort()
      }
    }
  }, [])

  useEffect(() => {
    addParticleSystem()
    if (playerRef.current.state === 'playing') {
      startParticleSystem()
    }
  }, [track?.data?.theme?.particleSystem?.type, waveformData])

  function handleMouseMove(position) {
    if (mousePosition !== 0) {
      setMousePosition(position)
    }
  }

  function handleMouseDown(position) {
    if (!loading) {
      setMousePosition(position)
    }
  }

  function handleMouseUp(position) {
    if (!loading) {
      const playPosition = Math.max(0, position * duration - 1)
      if (playbackEnabled) {
        if (player.state === 'playing') {
          const source = createSource(player, player.buffer)
          source.start(0, playPosition)
          player.ellapsedTime = playPosition
          player.startTime = player.audioContext.currentTime
        } else if (player.state === 'paused') {
          playTrack(playPosition)
        }
      }
      setMousePosition(0)
    }
  }

  async function handlePlayButton() {
    if (playbackEnabled) {
      if (player.state === 'paused') {
        playTrack(player.pausedTime)
      } else if (player.state === 'playing') {
        player.pausedTime = getCurrentTime()
        player.audioContext.suspend()
        player.state = 'paused'
        stopParticleSystem()
      }
    }
  }

  function getCurrentTime() {
    return player.audioContext && player.buffer
      ? player.ellapsedTime + player.audioContext.currentTime - player.startTime
      : 0
  }

  async function playTrack(offset) {
    if (!player.audioContext) {
      await loadTrack()
    } else {
      createSource(player, player.buffer)
    }
    if (!player.unmounted) {
      player.state = 'playing'
      player.songLookaheadTrigger = true
      player.startTime = player.audioContext.currentTime
      player.ellapsedTime = offset
      player.source.start(0, getCurrentTime())
      player.audioContext.resume()
      startParticleSystem()
    }
  }

  async function loadTrack() {
    return new Promise(async (resolve, reject) => {
      setLoading(true)
      createAudioContext()
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_ENDPOINT}/tracks/${track.id}/play`,
        )
        const { url } = await response.json()
        const request = new XMLHttpRequest()
        player.request = request
        player.state = 'loading'
        request.open('GET', url, true)
        request.responseType = 'arraybuffer'
        request.onload = () => {
          if (!player.unmounted) {
            let audioData = request.response
            setLoading(false)
            player.audioContext.decodeAudioData(
              audioData,
              (buffer) => {
                player.buffer = buffer
                createSource(player, buffer)
                resolve()
              },
              (e) => {
                console.error(`Error with decoding audio data ${e.error}`)
              },
            )
          }
        }

        request.send()
      } catch (e) {
        setLoading(false)
        player.error = e
        reject(e)
      }
    })
  }

  function createAudioContext() {
    if (!player.audioContext) {
      player.audioContext = new AudioContext()
      player.analyzer = player.audioContext.createAnalyser()
      player.analyzer.fftSize = 4096
      player.gainNode = player.audioContext.createGain()
      player.gainNode.gain.value = volume
      player.analyzer.connect(player.gainNode)
      player.gainNode.connect(player.audioContext.destination)
      player.audioContext.resume()
    }
  }

  function handleVolumeChange(value) {
    window.localStorage.setItem('volume', value)
    if (player.gainNode) {
      player.gainNode.gain.linearRampToValueAtTime(
        value,
        player.audioContext.currentTime + 0.025,
      )
    }
    setVolume(value)
  }

  function cloneAudioBuffer(fromAudioBuffer) {
    const audioBuffer = new AudioBuffer({
      length: fromAudioBuffer.length,
      numberOfChannels: fromAudioBuffer.numberOfChannels,
      sampleRate: fromAudioBuffer.sampleRate,
    })
    for (
      let channelI = 0;
      channelI < audioBuffer.numberOfChannels;
      ++channelI
    ) {
      const samples = fromAudioBuffer.getChannelData(channelI)
      audioBuffer.copyToChannel(samples, channelI)
    }
    return audioBuffer
  }

  function createSource(player, buffer) {
    const loop = track?.data?.loop || {}
    if (player.source) {
      player.source.stop()
      player.source.disconnect()
    }
    player.source = new AudioBufferSourceNode(player.audioContext)
    player.source.connect(player.analyzer)
    player.source.buffer = cloneAudioBuffer(buffer)

    player.source.addEventListener('ended', () => {
      onTrackEnded()
    })

    setupLoop(loop)

    return player.source
  }

  function setupLoop(loop) {
    player.source.loopEnd = player.source.buffer.duration
    player.source.loopStart = loop.loopTo

    if (loop.loopPointType !== 'end') {
      player.source.loopEnd = parseInt(loop.loopFrom, 10)
    }

    player.source.loop = loop?.count > 0
  }

  function onTrackEnded() {
    if (getCurrentTime() > duration - 1) {
      player.audioContext.suspend()
      player.ellapsedTime = 0
      player.startTime = player.audioContext.currentTime
      player.pausedTime = 0
      player.state = 'paused'
      setCurrentPosition(1)
    }
  }

  function addParticleSystem() {
    if (player.particleSystem) {
      player.particleSystem.stop()
      player.particleSystem = null
    }
    if (particleSystemData) {
      player.particleSystem = createParticleSystem({
        background: backgroundParticleCanvasRef.current,
        foreground: foregroundParticleCanvasRef.current,
        data: { ...particleSystemData },
        player,
        waveData: waveformData,
      })
    }
  }

  function startParticleSystem() {
    if (player.particleSystem) {
      player.particleSystem.start()
    }
  }

  function stopParticleSystem() {
    if (player.particleSystem) {
      player.particleSystem.stop()
    }
  }

  return (
    <div className="relative">
      <div
        className={clsx('flex flex-row items-center relative', {
          'opacity-40': !playbackEnabled,
        })}
      >
        <div className="px-6 w-[100px] h-[30px] text-center ">
          <button onClick={handlePlayButton}>
            {loading ? (
              <LoadingSpinner />
            ) : (
              <>
                {player.state === 'playing' ? (
                  <Pause fill={fgColor} />
                ) : (
                  <Play fill={fgColor} />
                )}
              </>
            )}
          </button>
        </div>
        <div className="absolute pointer-events-none h-[250px] top-[-50px] w-full">
          <canvas
            ref={backgroundParticleCanvasRef}
            className="block absolute"
          ></canvas>
        </div>
        <div className="w-full relative flex-1 h-[150px]">
          {waveformData && (
            <WaveformCanvas
              playerRef={playerRef}
              theme={track.theme}
              waveformData={waveformData?.drawableData}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
            ></WaveformCanvas>
          )}
          {!player?.audioContext ? (
            <div className="absolute flex w-full text-center top-[-28px]">
              <div className="w-[40%]">Primary Color</div>
              <div className="w-[30%]">Mouse Over Color</div>
              <div className="w-[30%]">Background Color</div>
            </div>
          ) : null}
        </div>
        <div className="absolute pointer-events-none h-[250px] top-[-50px] w-full">
          <canvas
            ref={foregroundParticleCanvasRef}
            className="block absolute"
          ></canvas>
        </div>
      </div>
      {!playbackEnabled && (
        <div className="absolute h-full top-0 pb-8 flex justify-center items-center w-full">
          <div className="p-4 bg-gray-bright drop-shadow-2xl rounded">
            Play back disabled while file conversion completes.
          </div>
        </div>
      )}
      <div className="flex flex-row relative">
        <div className="text-sm text-white-dark font-bold flex flex-row items-center">
          {track.name}
          {track.album ? ` - ${track.album}` : ''}
        </div>
        <div className="mt-2 flex flex-1 justify-end">
          <Volume
            value={volume}
            onChange={handleVolumeChange}
            color={fgColor}
          />
          <Duration
            current={getCurrentTime()}
            total={duration}
            mousePosition={mousePosition}
          />
        </div>
      </div>
    </div>
  )
}

export default WaveformPlayer
